import { getAssetIconByAddress, getAssetIconBySymbol } from 'abis/set/asset-icons'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import type { CurrencyMetadata } from 'indices-list'
import { zeroAddress } from 'viem'
import { arbitrum, avalanche, base, bsc, optimism, polygon } from 'viem/chains'

type CurrencyTypeIndexV1 = 'index-v1'
type CurrencyTypeIndexV2 = 'index-v2'
type CurrencyTypeIndexV3 = 'index-v3'
type CurrencyTypeSavingsVault = 'savings-vault'
type CurrencyTypeNative = 'native'
type CurrencyTypeToken = 'token'
type CurrencyTypePHTR = 'PHTR'
type CurrencyTypeStakedPHTR = 'staked-PHTR'

export type CurrencyType =
  | CurrencyTypeIndexV1
  | CurrencyTypeIndexV2
  | CurrencyTypeIndexV3
  | CurrencyTypeSavingsVault
  | CurrencyTypeNative
  | CurrencyTypeToken
  | CurrencyTypePHTR
  | CurrencyTypeStakedPHTR

export const ethereumCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Mainnet,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Mainnet,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  PHTR: {
    address: '0xe1fc4455f62a6e89476f1072530c20cf1a0622da',
    symbol: 'PHTR',
    name: 'Phuture',
    decimals: 18,
    icon: getAssetIconBySymbol('PHTR'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Mainnet,
    type: 'PHTR',
    coingeckoId: 'Phuture',
    permit: undefined,
  },
  ePHTR: {
    address: '0x3b9805E163b3750e7f13a26B06F030f2d3b799F5',
    symbol: 'ePHTR',
    name: 'ePhuture',
    decimals: 18,
    icon: getAssetIconBySymbol('EPHTR'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Mainnet,
    type: 'staked-PHTR',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const avalancheCurrencies: { [key: string]: CurrencyMetadata } = {
  AVAX: {
    address: zeroAddress,
    symbol: 'AVAX',
    name: 'Avalanche',
    decimals: 18,
    icon: getAssetIconBySymbol('AVAX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDT: {
    address: '0x9702230a8ea53601f5cd2dc00fdbc13d4df4a8c7',
    symbol: 'USDT',
    name: 'TetherToken',
    decimals: 6,
    icon: getAssetIconBySymbol('USDT'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  JOE: {
    address: '0x6e84a6216ea6dacc71ee8e6b0a5b7322eebc0fdd',
    symbol: 'JOE',
    name: 'JoeToken',
    decimals: 18,
    icon: getAssetIconByAddress('0x6e84a6216ea6dacc71ee8e6b0a5b7322eebc0fdd'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  COQ: {
    address: '0x420fca0121dc28039145009570975747295f2329',
    symbol: 'COQ',
    name: 'COQINU',
    decimals: 18,
    icon: getAssetIconByAddress('0x420fca0121dc28039145009570975747295f2329'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  CAI: {
    address: '0x48f88a3fe843ccb0b5003e70b4192c1d7448bef0',
    symbol: 'CAI',
    name: 'Colony Avalanche Index',
    decimals: 18,
    icon: getAssetIconBySymbol('CAI'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'index-v1',
    coingeckoId: 'colony-avalanche-index',
    permit: undefined,
  },
  SAVAX: {
    address: '0x2b2c81e08f1af8835a78bb2a90ae924ace0ea4be',
    symbol: 'SAVAX',
    name: 'Staked AVAX',
    decimals: 18,
    icon: getAssetIconBySymbol('SAVAX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Avalanche,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const avalancheFujiCurrencies: { [key: string]: CurrencyMetadata } = {
  AVAX: {
    address: zeroAddress,
    symbol: 'AVAX',
    name: 'Avalanche Testnet',
    decimals: 18,
    icon: getAssetIconBySymbol('AVAX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.AvalancheFuji,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x4a0d1092e9df255cf95d72834ea9255132782318',
    symbol: 'USDC',
    name: 'USD Coin Testnet',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.AvalancheFuji,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const polygonMumbaiCurrencies: { [key: string]: CurrencyMetadata } = {
  MATIC: {
    address: zeroAddress,
    symbol: 'MATIC',
    name: 'Matic Testnet',
    decimals: 18,
    icon: getAssetIconBySymbol('MATIC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.PolygonMumbai,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x742DfA5Aa70a8212857966D491D67B09Ce7D6ec7',
    symbol: 'USDC',
    name: 'USD Coin Testnet',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.PolygonMumbai,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  LINK: {
    address: '0x326c977e6efc84e512bb9c30f76e30c160ed06fb',
    symbol: 'LINK',
    name: 'LINK Testnet',
    decimals: 18,
    icon: getAssetIconBySymbol('LINK'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.PolygonMumbai,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const optimismSepoliaCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.SepoliaOptimism,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x488327236b65c61a6c083e8d811a4e0d3d1d4268',
    symbol: 'USDC',
    name: 'USD Coin Testnet',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.SepoliaOptimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  LINK: {
    address: '0xe4ab69c077896252fafbd49efd26b5d171a32410',
    symbol: 'LINK',
    name: 'LINK Testnet',
    decimals: 18,
    icon: getAssetIconBySymbol('LINK'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.SepoliaOptimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const arbitrumCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  ARTXAI: {
    address: '0x8e991e9edc300a538ce72186f5bcbc94f4bb9c80',
    symbol: 'ARTXAI',
    name: 'ARTX AI Smart Fund',
    decimals: 18,
    icon: getAssetIconBySymbol('ARTXAI'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'index-v2',
    coingeckoId: undefined,
    permit: undefined,
  },
  ARB: {
    address: '0x912CE59144191C1204E64559FE8253a0e49E6548',
    symbol: 'ARB',
    name: 'Arbitrum ',
    decimals: 18,
    icon: getAssetIconBySymbol('ARB'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
  USDT: {
    address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9',
    symbol: 'USDT',
    name: 'Tether USD',
    decimals: 6,
    icon: getAssetIconBySymbol('USDT'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
  USDC: {
    address: '0xaf88d065e77c8cc2239327c5edb3a432268e5831',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
  LINK: {
    address: '0xf97f4df75117a78c1a5a0dbb814af92458539fb4',
    symbol: 'LINK',
    name: 'LINK',
    decimals: 18,
    icon: getAssetIconBySymbol('LINK'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
  DAI: {
    address: '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1',
    symbol: 'DAI',
    name: 'Dai Stablecoin',
    decimals: 18,
    icon: getAssetIconByAddress('0xda10009cbd5d07dd0cecc66161fc93d7c9000da1'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
  WSTETH: {
    address: '0x5979d7b546e38e414f7e9822514be443a4800529',
    symbol: 'WSTETH',
    name: 'Wrapped liquid staked Ether 2.0',
    decimals: 18,
    icon: getAssetIconByAddress('0x5979d7b546e38e414f7e9822514be443a4800529'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  WEETH: {
    address: '0x35751007a407ca6feffe80b3cb397736d2cf4dbe',
    symbol: 'WEETH',
    name: 'Wrapped eETH',
    decimals: 18,
    icon: getAssetIconByAddress('0x35751007a407ca6feffe80b3cb397736d2cf4dbe'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  RETH: {
    address: '0xec70dcb4a1efa46b8f2d97c310c9c4790ba5ffa8',
    symbol: 'RETH',
    name: 'Rocket Pool ETH',
    decimals: 18,
    icon: getAssetIconByAddress('0xec70dcb4a1efa46b8f2d97c310c9c4790ba5ffa8'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  EZETH: {
    address: '0x2416092f143378750bb29b79ed961ab195cceea5',
    symbol: 'EZETH',
    name: 'Renzo Restaked ETH',
    decimals: 18,
    icon: getAssetIconByAddress('0x2416092f143378750bb29b79ed961ab195cceea5'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  CBETH: {
    address: '0x1debd73e752beaf79865fd6446b0c970eae7732f',
    symbol: 'CBETH',
    name: 'Coinbase Wrapped Staked ETH',
    decimals: 18,
    icon: getAssetIconByAddress('0x1debd73e752beaf79865fd6446b0c970eae7732f'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  SFRXETH: {
    address: '0x95ab45875cffdba1e5f451b950bc2e42c0053f39',
    symbol: 'SFRXETH',
    name: 'Staked Frax Ether',
    decimals: 18,
    icon: getAssetIconByAddress('0x95ab45875cffdba1e5f451b950bc2e42c0053f39'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  RSETH: {
    address: '0x4186bfc76e2e237523cbc30fd220fe055156b41f',
    symbol: 'RSETH',
    name: 'KelpDao Restaked ETH',
    decimals: 18,
    icon: getAssetIconByAddress('0x4186bfc76e2e237523cbc30fd220fe055156b41f'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  WBTC: {
    address: '0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f',
    symbol: 'WBTC',
    name: 'Wrapped BTC',
    decimals: 8,
    icon: getAssetIconByAddress('0x2f2a2543b76a4166549f7aab2e75bef0aefc5b0f'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Arbitrum,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const baseCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x833589fcd6edb6e08f4c7c32d4f71b54bda02913',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDT: {
    address: '0xfde4c96c8593536e31f229ea8f37b2ada2699bb2',
    symbol: 'USDT',
    name: 'Tether USD',
    decimals: 6,
    icon: getAssetIconBySymbol('USDT'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  WBTC: {
    address: '0x0555e30da8f98308edb960aa94c0db47230d2b9c',
    symbol: 'WBTC',
    name: 'Wrapped BTC',
    decimals: 8,
    icon: getAssetIconBySymbol('WBTC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDbC: {
    address: '0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca',
    symbol: 'USDbC',
    name: 'USD Base Coin',
    decimals: 6,
    icon: getAssetIconByAddress('0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  JMX: {
    address: '0x83190636c344cf1220ffcbfc0c1198f7867b3667',
    symbol: 'JMX',
    name: 'JOOCE Memecoin index',
    decimals: 18,
    icon: getAssetIconBySymbol('JMX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'index-v2',
    coingeckoId: undefined,
    permit: undefined,
  },
  'PWMOG-1': {
    address: '0x61a287fc37559c71ba7cf6c35f3a5716dc91d265',
    symbol: 'pwMog',
    name: 'Phuture Wrapped Mog Coin',
    decimals: 18,
    icon: getAssetIconByAddress('0x61a287fc37559c71ba7cf6c35f3a5716dc91d265'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  'PWMOG-2': {
    address: '0x66af6b21aad17849bf8db8844b26d5cdd1b7ac86',
    symbol: 'pwMog',
    name: 'Phuture Wrapped Mog Coin',
    decimals: 18,
    icon: getAssetIconByAddress('0x66af6b21aad17849bf8db8844b26d5cdd1b7ac86'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  'PWMOG-3': {
    address: '0xeff13712b5efacf5213f9a76bc58d89f037d71d3',
    symbol: 'pwMog',
    name: 'Phuture Wrapped Mog Coin',
    decimals: 18,
    icon: getAssetIconByAddress('0xeff13712b5efacf5213f9a76bc58d89f037d71d3'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  BRETT: {
    address: '0x532f27101965dd16442e59d40670faf5ebb142e4',
    symbol: 'BRETT',
    name: 'Brett',
    decimals: 18,
    icon: getAssetIconByAddress('0x532f27101965dd16442e59d40670faf5ebb142e4'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  TRUMP: {
    address: '0x57f5fbd3de65dfc0bd3630f732969e5fb97e6d37',
    symbol: 'TRUMP',
    name: 'MAGA',
    decimals: 9,
    icon: getAssetIconByAddress('0x57f5fbd3de65dfc0bd3630f732969e5fb97e6d37'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Base,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const bscCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Bsc,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 18,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Bsc,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  DOGE: {
    address: '0xba2ae424d960c26247dd6c32edc70b295c744c43',
    symbol: 'DOGE',
    name: 'Dogecoin',
    decimals: 8,
    icon: getAssetIconByAddress('0xba2ae424d960c26247dd6c32edc70b295c744c43'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Bsc,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  BONK: {
    address: '0xa697e272a73744b343528c3bc4702f2565b2f422',
    symbol: 'Bonk',
    name: 'Bonk',
    decimals: 5,
    icon: getAssetIconByAddress('0xa697e272a73744b343528c3bc4702f2565b2f422'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Bsc,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  FLOKI: {
    address: '0xfb5b838b6cfeedc2873ab27866079ac55363d37e',
    symbol: 'FLOKI',
    name: 'FLOKI',
    decimals: 9,
    icon: getAssetIconByAddress('0xfb5b838b6cfeedc2873ab27866079ac55363d37e'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Bsc,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const optimismCurrencies: { [key: string]: CurrencyMetadata } = {
  ETH: {
    address: zeroAddress,
    symbol: 'ETH',
    name: 'Ethereum',
    decimals: 18,
    icon: getAssetIconBySymbol('ETH'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'native',
    coingeckoId: undefined,
    permit: undefined,
  },
  OP: {
    address: '0x4200000000000000000000000000000000000042',
    symbol: 'OP',
    name: 'Optimism ',
    decimals: 18,
    icon: getAssetIconBySymbol('OP'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  SNX: {
    address: '0x8700daec35af8ff88c16bdf0418774cb3d7599b4',
    symbol: 'SNX',
    name: 'Synthetix Network Token',
    decimals: 18,
    icon: getAssetIconBySymbol('SNX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  LINK: {
    address: '0x350a791bfc2c21f9ed5d10980dad2e2638ffa7f6',
    symbol: 'LINK',
    name: 'ChainLink Token',
    decimals: 18,
    icon: getAssetIconBySymbol('LINK'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  LDO: {
    address: '0xfdb794692724153d1488ccdbe0c56c252596735f',
    symbol: 'LDO',
    name: 'Lido DAO Token',
    decimals: 18,
    icon: getAssetIconBySymbol('LDO'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  CRV: {
    address: '0x0994206dfe8de6ec6920ff4d779b0d950605fb53',
    symbol: 'CRV',
    name: 'Curve DAO Token',
    decimals: 18,
    icon: getAssetIconBySymbol('CRV'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  BAL: {
    address: '0xfe8b128ba8c78aabc59d4c64cee7ff28e9379921',
    symbol: 'BAL',
    name: 'Balancer',
    decimals: 18,
    icon: getAssetIconBySymbol('BAL'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  AAVE: {
    address: '0x76fb31fb4af56892a25e32cfc43de717950c9278',
    symbol: 'AAVE',
    name: 'Aave Token',
    decimals: 18,
    icon: getAssetIconBySymbol('AAVE'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Optimism,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
}

export const polygonCurrencies: { [key: string]: CurrencyMetadata } = {
  MATICX: {
    address: '0xfa68fb4628dff1028cfec22b4162fccd0d45efb6',
    symbol: 'MATICX',
    name: 'Liquid Staking Matic (PoS)',
    decimals: 18,
    icon: getAssetIconBySymbol('MATICX'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Polygon,
    type: 'token',
    coingeckoId: undefined,
    permit: undefined,
  },
  USDC: {
    address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',
    symbol: 'USDC',
    name: 'USD Coin',
    decimals: 6,
    icon: getAssetIconBySymbol('USDC'),
    defaultQuoteAmount: Big(1),
    chainId: NetworkType.Polygon,
    type: 'token',
    coingeckoId: undefined,
    permit: 'eip2612',
  },
}

export const acceptableCurrencies: { [chain: number]: { [key: string]: CurrencyMetadata } } = {
  [optimism.id]: optimismCurrencies,
  [polygon.id]: polygonCurrencies,
  [arbitrum.id]: arbitrumCurrencies,
  [avalanche.id]: avalancheCurrencies,
  [base.id]: baseCurrencies,
  [bsc.id]: bscCurrencies,
}

export const inputCurrencies: { [key: string]: CurrencyMetadata[] } = {
  cai: [
    avalancheCurrencies.AVAX,
    avalancheCurrencies.USDC,
    avalancheCurrencies.USDT,
    avalancheCurrencies.JOE,
    avalancheCurrencies.COQ,
  ], //INFO:  AVAX, USDC
  ephtr: [ethereumCurrencies.PHTR],
  artxai: [
    arbitrumCurrencies.ETH,
    arbitrumCurrencies.USDC,
    arbitrumCurrencies.USDT,
    arbitrumCurrencies.DAI,
    arbitrumCurrencies.ARB,
  ],
  jmx: [baseCurrencies.ETH, baseCurrencies.USDC, baseCurrencies.USDT, baseCurrencies.WBTC],
}
